import { MULTITENANT_CONSTANTS, TENANT } from "../Resources/Multitenant/tenantConstants";
import { URL_BASE_V2, URL_SGCA } from "./servicesConstants";
import {
  AGENDA_HORIZON_DAYS,
  AGENDA_MAX_DURATION_SECONDS,
  AGENDA_DEFAULT_LIVE_SECONDS,
  AGENDA_TIMEZONE,
  addDays,
  hasFinish,
  madridDayOfSeconds,
  madridToday,
} from "../Utils/agendaDateUtils";

// Capa de acceso a la API de la página "Agenda" (OTT-2011, Fase 1).
// Contrato: pantallas 18-20 de "Agenda Especificacion" (Claude Design) y docs/agenda-analisis.md.
// No reutiliza searchBuscar (buscarService.js): esa función envía siempre grouped=true, searchin y
// sort=-date, y el modo Agenda se activa con upcoming=true y nunca con grouped.

export const AGENDA_SORT = "start";
export const AGENDA_DAYS_LIMIT = 7;
// Con group=day: tarjetas por día, siempre 5 (el máximo de columnas); el front pinta las que caben.
export const AGENDA_CARDS_PER_DAY = 5;
// Vista de día: lotes de 8 en móvil y 15 en el resto (pantallas 03/13).
export const AGENDA_DAY_BATCH_MOBILE = 8;
export const AGENDA_DAY_BATCH = 15;

export const AGENDA_ERROR = {
  BAD_GROUP: "bad-group",
  BAD_DAYS_LIMIT: "bad-days-limit",
  BAD_SORT: "bad-sort",
  BAD_DATE: "bad-date",
  NODE_NOT_FOUND: "node-not-found",
};
const INVALID_NODE_TYPE_MESSAGE = "Invalid node type";

const tenantServer = () => MULTITENANT_CONSTANTS[TENANT].tenantServer;
const sgcaUrl = (path, query) => `${URL_BASE_V2}${URL_SGCA}${tenantServer()}/${path}?${query.toString()}`;

// ---------------------------------------------------------------------------------------------
// Errores
// ---------------------------------------------------------------------------------------------

/** Cuerpo de error del backend: `{"error":"texto"}` o `{"error":{"message","key"}}`. */
const parseErrorBody = (raw) => {
  try {
    const body = typeof raw === "string" ? JSON.parse(raw) : raw;
    const error = body?.error;
    if (typeof error === "string") return { message: error };
    return { key: error?.key, message: error?.message };
  } catch (e) {
    return { message: typeof raw === "string" ? raw.slice(0, 200) : undefined };
  }
};

const createApiError = (status, { key, message } = {}, url) => {
  const error = new Error(`GET ${url || ""} failed ${status}${key ? ` ${key}` : ""}${message ? `: ${message}` : ""}`);
  error.status = status;
  error.key = key;
  error.apiMessage = message;
  return error;
};

/**
 * Lee una respuesta de la API y la trata como error si no es 2xx o si KrakenD envolvió un error
 * del backend en un HTTP 200: `{ "error_error": { "http_status_code": 400, "http_body": "…" } }`
 * (contrato §19). El error lleva `status` (el de http_status_code) y `key` (p.ej. node-not-found).
 */
export const readAgendaResponse = async (response) => {
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw createApiError(response.status, parseErrorBody(text), response.url);
  }
  const body = await response.json();
  if (body && body.error_error) {
    const { http_status_code: status, http_body: httpBody } = body.error_error;
    throw createApiError(status || 500, parseErrorBody(httpBody), response.url);
  }
  return body;
};

export const isNodeNotFoundError = (error) => error?.status === 404 && error?.key === AGENDA_ERROR.NODE_NOT_FOUND;

const getJson = (url, signal) =>
  fetch(url, { method: "GET", headers: { "Content-Type": "application/json" }, signal }).then(readAgendaResponse);

// ---------------------------------------------------------------------------------------------
// Nodos de filtro (type=agenda)
// ---------------------------------------------------------------------------------------------

/**
 * GET /v2/sgca/{tenant}/nodes-root?interfaz={id}&type=agenda — filtros propios de la Agenda
 * (contrato §18.2). `data: []` => no se muestra el botón "Filtros".
 * Supuesto: si el backend todavía no conoce el tipo (400 "Invalid node type", como en PRO a
 * 2026-09-30), se trata como "sin filtros" en vez de como un error de la página.
 */
export const getAgendaNodesRoot = (interfazId, { signal } = {}) => {
  const query = new URLSearchParams();
  if (interfazId != null) query.set("interfaz", interfazId);
  query.set("type", "agenda");
  return getJson(sgcaUrl("nodes-root", query), signal).catch((error) => {
    if (error?.status === 400 && error?.apiMessage === INVALID_NODE_TYPE_MESSAGE) return { data: [] };
    throw error;
  });
};

/** GET /v2/sgca/{tenant}/nodes-children/{id}?interfaz={id} — sin type: heredan el del padre. */
export const getAgendaNodesChildren = (nodeId, interfazId, { signal } = {}) => {
  const query = new URLSearchParams();
  if (interfazId != null) query.set("interfaz", interfazId);
  return getJson(sgcaUrl(`nodes-children/${nodeId}`, query), signal);
};

// ---------------------------------------------------------------------------------------------
// /search en modo Agenda (upcoming=true)
// ---------------------------------------------------------------------------------------------

/**
 * @typedef {Object} AgendaDayMeta
 * @property {string} date YYYY-MM-DD (Europe/Madrid)
 * @property {number} count partidos de ese día con los filtros aplicados
 *
 * @typedef {Object} AgendaDay
 * @property {string} date
 * @property {number} count
 * @property {Array<Object>} data hasta `limit` contenidos, ordenados por start
 *
 * @typedef {Object} AgendaDaysResponse Respuesta D (group=day)
 * @property {{total:number, group:"day", timezone:string, today:string, until:string, days:AgendaDayMeta[]}} meta
 *   `meta.days`: todos los días del horizonte con count > 0 (con filtros), no solo los de la página.
 * @property {AgendaDay[]} days los días de esta página
 * @property {number} limit
 * @property {number} days_limit
 * @property {number} page
 * @property {string} sort
 *
 * @typedef {Object} AgendaDayResponse Respuesta A (vista de día: upcoming=true + from = to)
 * @property {{filter_count:number, timezone?:string, today?:string, until?:string}} meta
 * @property {Array<Object>} data
 * @property {number} limit
 * @property {number} page
 * @property {string} sort
 *
 * Contenido: el mismo objeto que en el buscador, con `start`/`finish` en epoch SEGUNDOS
 * (`finish` vacío = null o 0) y `category` = `{ id, code, translations[] }` (no hay category.name).
 */

const baseAgendaQuery = ({ nodes, interfazId }) => {
  const query = new URLSearchParams();
  query.set("upcoming", "true");
  query.set("sort", AGENDA_SORT);
  if (nodes && nodes.length) query.set("nodes", nodes.join(","));
  if (interfazId != null) query.set("interfaz", interfazId);
  return query;
};

const contractDays = ({ nodes, page = 1, daysLimit = AGENDA_DAYS_LIMIT, limit = AGENDA_CARDS_PER_DAY, interfazId }, signal) => {
  const query = baseAgendaQuery({ nodes, interfazId });
  query.set("group", "day");
  query.set("limit", limit);
  query.set("days_limit", daysLimit);
  query.set("page", page);
  return getJson(sgcaUrl("search", query), signal);
};

const contractDay = ({ day, nodes, page = 1, limit = AGENDA_DAY_BATCH, interfazId }, signal) => {
  const query = baseAgendaQuery({ nodes, interfazId });
  query.set("from", day);
  query.set("to", day);
  query.set("limit", limit);
  query.set("page", page);
  return getJson(sgcaUrl("search", query), signal);
};

// ---------------------------------------------------------------------------------------------
// Adaptador temporal (pantalla 20, "Desarrollo en paralelo al backend")
// ---------------------------------------------------------------------------------------------
// ÚNICO punto de cambio: cuando el backend de PRO tenga upcoming/group=day, basta con borrar
// esta sección y `runAgendaCall` (las llamadas del contrato ya son las de arriba).
//
// Mientras no existan upcoming ni group=day, la única llamada que responde sin texto es la del
// buscador con su sección implícita (grouped=true&section=0): se piden todos los contenidos del
// horizonte y en front se aplica la regla de upcoming y se agrupan por día de Madrid.

const ADAPTER_PAGE_SIZE = 200;
// Tope de seguridad: el horizonte de 14 días de tiivii ronda el centenar de directos.
const ADAPTER_MAX_PAGES = 10;
const ADAPTER_CACHE_MS = 60 * 1000;
const EXCLUDED_TAG = "demotiivii";

const isLiveContent = (item) => {
  const type = item?.type?.name ?? item?.type;
  return typeof type === "string" && type.toLowerCase().includes("live");
};

/**
 * Normaliza `start`/`finish` a epoch segundos.
 * Supuesto: con orch < 5.1.24 (PRO a 2026-09-30) la respuesta no trae `start` ni `finish`, solo
 * `date` (ISO con el desfase de Madrid, que para un directo es su inicio): se usa `date` como
 * inicio y `finish` queda vacío (regla start + 2 h).
 */
export const normalizeAdapterItem = (item) => {
  const start = item?.start || (item?.date ? Math.floor(Date.parse(item.date) / 1000) : null);
  return { ...item, start: Number.isFinite(start) ? start : null, finish: item?.finish || null };
};

/** Regla de upcoming=true (contrato §18.4) aplicada en front. `nowMs` inyectable para tests. */
export const isUpcomingItem = (item, nowMs = Date.now()) => {
  if (!isLiveContent(item) || !item.start) return false;
  if (Array.isArray(item.tags) && item.tags.includes(EXCLUDED_TAG)) return false;
  const now = nowMs / 1000;
  if (hasFinish(item.finish)) {
    if (item.finish - item.start > AGENDA_MAX_DURATION_SECONDS) return false;
    return item.finish > now;
  }
  return item.start > now - AGENDA_DEFAULT_LIVE_SECONDS;
};

/**
 * Filtra y agrupa por día de Madrid dentro del horizonte [today, until]. Un directo que empezó
 * antes de hoy y sigue en curso cuenta como de hoy (contrato: "el backend lo agrupa en hoy").
 * @returns {{today:string, until:string, items:Array<Object>}} items con `_day`, ordenados por start
 */
export const buildAdapterPool = (rawItems, nowMs = Date.now()) => {
  const today = madridToday(nowMs);
  const until = addDays(today, AGENDA_HORIZON_DAYS - 1);
  const items = (rawItems || [])
    .map(normalizeAdapterItem)
    .filter((item) => isUpcomingItem(item, nowMs))
    .map((item) => {
      const day = madridDayOfSeconds(item.start);
      return { ...item, _day: day < today ? today : day };
    })
    .filter((item) => item._day <= until)
    .sort((a, b) => a.start - b.start || a.id - b.id);
  return { today, until, items };
};

const stripAdapterFields = ({ _day, ...item }) => item;

/** Respuesta D a partir del pool del adaptador. */
export const buildDaysResponse = (pool, { page = 1, daysLimit = AGENDA_DAYS_LIMIT, limit = AGENDA_CARDS_PER_DAY } = {}) => {
  const byDay = new Map();
  pool.items.forEach((item) => {
    if (!byDay.has(item._day)) byDay.set(item._day, []);
    byDay.get(item._day).push(item);
  });
  const metaDays = [...byDay.keys()].sort().map((date) => ({ date, count: byDay.get(date).length }));
  const pageDays = metaDays.slice((page - 1) * daysLimit, page * daysLimit);
  return {
    meta: { total: pool.items.length, group: "day", timezone: AGENDA_TIMEZONE, today: pool.today, until: pool.until, days: metaDays },
    days: pageDays.map(({ date, count }) => ({ date, count, data: byDay.get(date).slice(0, limit).map(stripAdapterFields) })),
    limit,
    days_limit: daysLimit,
    page,
    sort: AGENDA_SORT,
  };
};

/** Respuesta A (vista de día) a partir del pool del adaptador. */
export const buildDayResponse = (pool, { day, page = 1, limit = AGENDA_DAY_BATCH } = {}) => {
  const dayItems = pool.items.filter((item) => item._day === day);
  return {
    meta: { filter_count: dayItems.length, timezone: AGENDA_TIMEZONE, today: pool.today, until: pool.until },
    data: dayItems.slice((page - 1) * limit, page * limit).map(stripAdapterFields),
    limit,
    page,
    sort: AGENDA_SORT,
  };
};

let adapterCache = { key: null, at: 0, promise: null };

/** Rechaza con AbortError en cuanto se aborta `signal`, sin cancelar `promise` (que puede estar
 * compartida en caché con otras llamadas). */
const withAbortSignal = (promise, signal) => {
  if (!signal) return promise;
  const abortError = () => {
    const error = new Error("Aborted");
    error.name = "AbortError";
    return error;
  };
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", onAbort));
  });
};

// La petición del pool no lleva el `signal` de quien la pide: se comparte (caché de 60 s) entre la
// lista, el lazy loading y la vista de día, y abortar una de ellas no debe romper a las demás
// (p.ej. el doble montaje de StrictMode en desarrollo). Cada llamada respeta su propio signal
// con withAbortSignal.
const fetchAdapterPool = ({ nodes, interfazId }, signal) => {
  const key = `${interfazId ?? ""}|${(nodes || []).join(",")}`;
  const now = Date.now();
  if (adapterCache.key === key && now - adapterCache.at < ADAPTER_CACHE_MS && adapterCache.promise) {
    return withAbortSignal(adapterCache.promise, signal);
  }
  const today = madridToday(now);
  const until = addDays(today, AGENDA_HORIZON_DAYS - 1);

  const load = async () => {
    const collected = [];
    for (let page = 1; page <= ADAPTER_MAX_PAGES; page += 1) {
      const query = new URLSearchParams();
      query.set("grouped", "true");
      query.set("section", "0");
      query.set("from", today);
      query.set("to", until);
      query.set("sort", AGENDA_SORT);
      query.set("limit", ADAPTER_PAGE_SIZE);
      query.set("page", page);
      if (nodes && nodes.length) query.set("nodes", nodes.join(","));
      if (interfazId != null) query.set("interfaz", interfazId);
      // eslint-disable-next-line no-await-in-loop
      const res = await getJson(sgcaUrl("search", query));
      const data = res?.data || (res?.sections || []).flatMap((s) => s.data || []);
      collected.push(...data);
      const total = res?.meta?.filter_count ?? res?.meta?.total ?? collected.length;
      if (!data.length || collected.length >= total) break;
    }
    return buildAdapterPool(collected);
  };

  const promise = load();
  adapterCache = { key, at: now, promise };
  // Una petición fallida no se queda en caché.
  promise.catch(() => {
    if (adapterCache.promise === promise) adapterCache = { key: null, at: 0, promise: null };
  });
  return withAbortSignal(promise, signal);
};

// ---------------------------------------------------------------------------------------------
// Selección contrato / adaptador
// ---------------------------------------------------------------------------------------------

// null = sin decidir; "contract" = el backend implementa upcoming/group=day; "adapter" = no.
let agendaApiMode = null;

const CONTRACT_ERROR_KEYS = [AGENDA_ERROR.BAD_DAYS_LIMIT, AGENDA_ERROR.BAD_SORT, AGENDA_ERROR.BAD_DATE, AGENDA_ERROR.NODE_NOT_FOUND];

/**
 * Supuesto: un 400 que no es uno de los errores propios del contrato significa que el backend
 * todavía no conoce el modo Agenda (PRO a 2026-09-30 responde 400 "Missing filter object in
 * query" a upcoming=true sin texto). Se decide una vez por sesión.
 */
const isModeUnsupportedError = (error) => error?.status === 400 && !CONTRACT_ERROR_KEYS.includes(error?.key);

const runAgendaCall = async (contractCall, adapterCall) => {
  if (agendaApiMode === "adapter") return adapterCall();
  try {
    const response = await contractCall();
    agendaApiMode = "contract";
    return response;
  } catch (error) {
    if (agendaApiMode === null && isModeUnsupportedError(error)) {
      agendaApiMode = "adapter";
      console.warn("[Agenda] El backend no admite upcoming=true: se usa el adaptador temporal.", error.message);
      return adapterCall();
    }
    throw error;
  }
};

/** Solo tests: vuelve al estado inicial (sin modo decidido y sin caché del adaptador). */
export const resetAgendaApiState = () => {
  agendaApiMode = null;
  adapterCache = { key: null, at: 0, promise: null };
};

/**
 * Lista por días (pantallas 01/05/12): upcoming=true&group=day&sort=start&limit=5&days_limit=7.
 * @param {{nodes?:number[], page?:number, daysLimit?:number, limit?:number, interfazId?:number|string}} params
 * @param {{signal?:AbortSignal}} [opts]
 * @returns {Promise<AgendaDaysResponse>}
 */
export const searchAgendaDays = (params = {}, { signal } = {}) =>
  runAgendaCall(
    () => contractDays(params, signal),
    () => fetchAdapterPool(params, signal).then((pool) => buildDaysResponse(pool, params)),
  );

/**
 * Vista de un día (pantallas 03/13): upcoming=true&from=D&to=D&sort=start&limit=8|15.
 * @param {{day:string, nodes?:number[], page?:number, limit?:number, interfazId?:number|string}} params
 * @param {{signal?:AbortSignal}} [opts]
 * @returns {Promise<AgendaDayResponse>}
 */
export const searchAgendaDay = (params, { signal } = {}) =>
  runAgendaCall(
    () => contractDay(params, signal),
    () => fetchAdapterPool(params, signal).then((pool) => buildDayResponse(pool, params)),
  );
